import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, MutationCache, QueryCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import * as Sentry from "@sentry/react";
import { captureError } from "@/lib/logger";

import Auth from "./pages/Auth";
import DriverLayout from "./components/layout/DriverLayout";
import RequireAuth from "./components/auth/RequireAuth";
import ContractDetail from "./pages/ContractDetail";
import SignDocument from "./pages/SignDocument";
import SignLink from "./pages/SignLink";
import NotFound from "./pages/NotFound";

/**
 * THE ONE LAZY ROUTE IN THIS APP, AND IT IS NOT AN OPTIMISATION.
 *
 * `ViewAgreement` reaches `pdfjs-dist`, which is several hundred kilobytes of parser and a
 * worker of its own. Every other page here is imported eagerly, so a static import would put
 * the whole PDF reader in the entry chunk — paid for on first paint by every driver, on every
 * visit, to read a balance. `React.lazy` is what keeps it in a chunk that is fetched the
 * moment somebody opens an agreement and never before.
 *
 * `SignLink` lazily imports the viewer for the same reason; the two share the chunk.
 */
const ViewAgreement = lazy(() => import("./pages/ViewAgreement"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => captureError(error),
  }),
  mutationCache: new MutationCache({
    onError: (error) => captureError(error),
  }),
});

/**
 * `/sign/witness` was the only public ceremony before deposit payers got one, and every
 * link already shared with a witness points at it. Those links cannot be recalled, so the
 * route stays — as a redirect to `/sign/link`.
 *
 * THE FRAGMENT HAS TO SURVIVE THE HOP: it is the whole credential, and a redirect that
 * dropped it would land the witness on the "this signing link is incomplete" notice with
 * nothing left to recover. `Navigate` is therefore given the full location parts rather
 * than a bare path string.
 */
const LegacyWitnessLink = () => {
  const location = useLocation();
  return (
    <Navigate
      replace
      to={{ pathname: "/sign/link", search: location.search, hash: location.hash }}
    />
  );
};

/** The chunk boundary made visible: the same skeleton the page itself opens on. */
const ViewAgreementFallback = () => (
  <div className="mx-auto w-full max-w-2xl space-y-4 p-4">
    <div className="h-6 w-48 animate-pulse rounded-md bg-muted" />
    <div className="h-96 w-full animate-pulse rounded-md bg-muted" />
    <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
  </div>
);

const App = () => (
  <Sentry.ErrorBoundary fallback={<p>Something went wrong. Our team has been notified.</p>} onError={(error) => captureError(error)}>
    <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Navigate to="/summary" replace />} />
            <Route path="/auth" element={<Auth />} />

            {/* PUBLIC, and it must stay public: nobody who signs by link has an account, and
                their token lives in the URL fragment. Declared before the authenticated
                signing route so the static segment is unambiguous. */}
            <Route path="/sign/link" element={<SignLink />} />
            <Route path="/sign/witness" element={<LegacyWitnessLink />} />

            {/* Authenticated, but OUTSIDE the layout chrome — a ceremony is a full-screen
                flow with nothing to wander off into mid-signature. */}
            <Route element={<RequireAuth />}>
              <Route path="/sign/:documentId" element={<SignDocument />} />

              {/* THE AT-REST READER, beside the ceremony and outside the chrome for the same
                  reason: reading an executed instrument is a full-screen, single-purpose
                  thing. A `/agreement` with no id is somebody who edited the address bar or
                  followed a truncated link; it goes to the documents list rather than to the
                  404, which would tell them their agreement does not exist. */}
              <Route path="/agreement" element={<Navigate to="/documents" replace />} />
              <Route
                path="/agreement/:documentId"
                element={
                  <Suspense fallback={<ViewAgreementFallback />}>
                    <ViewAgreement />
                  </Suspense>
                }
              />
            </Route>

            <Route element={<DriverLayout />}>
              <Route path="/contract/:contractId" element={<ContractDetail />} />
              <Route path="/:section" element={<ContractDetail />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
